import { useLayoutEffect, useRef, useState } from 'preact/hooks';

import { cx, Icon } from '@amumin/ui-core';

import {
  addDuration,
  DURATIONS,
  formatDuration,
  HOUR_COLS,
  hourOptions,
  inRange,
  isNight,
  MINUTE_COLS,
  minuteOptions,
  minutesOfTime,
  moveInGrid,
  pad2,
  timeOfMinutes,
  type DurationLabels,
  type GridKey,
} from './time.js';
import styles from './TimePicker.module.css';

// Tijdkiezer (verzoek oprichter 2026-09-24: "tijd instellen loopt niet lekker"). Twee tikken voor
// elke gewone tijd: eerst het uur (06–23, de nacht achter een knopje), dan de minuten (kwartieren
// groot, vijftallen kleiner). Bij "Tot" staan er duurknoppen boven ("1 uur · 16:30"). Op een brede
// pagina een popover onder het veld, op een telefoon (≤ 600 px) een sheet van onderen.
//
// Technisch een modaal <dialog> (toplaag): het blijft zichtbaar binnen een sheet met overflow,
// Escape en de focus-trap komen van de browser, en na sluiten gaat de focus terug naar de knop.

/** Teksten van de kiezer en het veld (i18n `a.timePicker`). */
export interface TimeFieldLabels extends DurationLabels {
  open: string;
  hourStep: string;
  minuteStep: string;
  hours: string;
  minutes: string;
  showNight: string;
  back: string;
  close: string;
  type: string;
  invalid: string;
  beforeStart: string;
  durations: string;
  orPickEnd: string;
  durationUntil: string;
}

export interface TimePickerProps {
  /** Id van het dialog-element (aria-controls van de klokknop). */
  id: string;
  /** Veldlabel, bv. "Van"; bovenaan de kiezer. */
  label: string;
  value: string;
  /** Begintijd voor de duurknoppen (alleen bij "Tot"). */
  durationFrom?: string | undefined;
  step?: number | undefined;
  min?: string | undefined;
  max?: string | undefined;
  labels: TimeFieldLabels;
  /** Het veld waaronder de popover hangt. */
  anchor: () => HTMLElement | null;
  onPick: (value: string) => void;
  /** Sluiten zonder kiezen (Escape, buiten tikken, sluitknop). */
  onClose: () => void;
  /** "Zelf typen": sluiten en het toetsenbord openen. */
  onType: () => void;
  /**
   * Geopend met een tik of de muis: de knop die de focus krijgt, toont geen dikke focusring (die
   * stond dubbel om het gevulde uur, review c-ux r3). De eerste toets zet de ring weer aan.
   */
  quietFocus?: boolean | undefined;
}

const SHEET_QUERY = '(max-width: 600px)';
const GAP = 6;
const EDGE = 8;

function chunk<T>(items: readonly T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size));
  return rows;
}

const GRID_KEYS: readonly string[] = [
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
];

function isSheet(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(SHEET_QUERY).matches;
}

/**
 * De kiezer zelf. Het veld rendert hem alleen zolang hij open is: elke keer vers, met het uur van
 * de huidige waarde geselecteerd (en de nacht open als die waarde in de nacht valt).
 */
export function TimePicker({
  id,
  label,
  value,
  durationFrom,
  step,
  min,
  max,
  labels,
  anchor,
  onPick,
  onClose,
  onType,
  quietFocus = false,
}: TimePickerProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [quiet, setQuiet] = useState(quietFocus);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const current = minutesOfTime(value);
  const currentHour = current === null ? null : Math.floor(current / 60);
  const currentMinute = current === null ? null : current % 60;

  const [stage, setStage] = useState<'hour' | 'minute'>('hour');
  const [hour, setHour] = useState<number | null>(null);
  const [night, setNight] = useState(() => isNight(value));
  const [active, setActive] = useState(() => {
    const idx = hourOptions(isNight(value)).indexOf(currentHour ?? 9);
    return idx < 0 ? 0 : idx;
  });
  // Na openen of een stapwissel: focus op de actieve knop van het raster.
  const focusGrid = useRef(true);

  const hours = hourOptions(night);
  const minutes = minuteOptions(step);
  const startValid = durationFrom !== undefined && minutesOfTime(durationFrom) !== null;
  const durationChoices = startValid
    ? DURATIONS.map((d) => ({ d, end: addDuration(durationFrom, d) }))
    : [];

  // Bij Tot: tijden op of vóór Van rustig grijs (niet uit: je kunt eerst Tot en dan Van zetten).
  const startMin = startValid ? minutesOfTime(durationFrom) : null;
  const beforeStart = (h: number, m: number) => startMin !== null && h * 60 + m <= startMin;

  const hourAllowed = (h: number) =>
    minutes.some((m) => inRange(timeOfMinutes(h * 60 + m), min, max));
  const minuteAllowed = (h: number, m: number) => inRange(timeOfMinutes(h * 60 + m), min, max);

  // Plaatsen onder (of boven) het veld; op een telefoon doet CSS het (sheet onderaan).
  const place = () => {
    const el = ref.current;
    const target = anchor();
    if (!el || !target || !el.open) return;
    if (isSheet()) {
      el.style.removeProperty('top');
      el.style.removeProperty('left');
      return;
    }
    const r = target.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const left = Math.max(EDGE, Math.min(r.left, vw - w - EDGE));
    const below = vh - r.bottom - GAP - EDGE;
    const above = r.top - GAP - EDGE;
    const top =
      below >= h || below >= above
        ? Math.min(r.bottom + GAP, Math.max(EDGE, vh - h - EDGE))
        : Math.max(EDGE, r.top - GAP - h);
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
  };

  // Openen vóór de eerste paint (geen flits in het midden van het scherm); weghalen uit de DOM
  // sluit het dialoog vanzelf.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !el.open) el.showModal();
    const onMove = () => place();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    const onCancel = (e: Event) => {
      e.preventDefault();
      closeRef.current();
    };
    el?.addEventListener('cancel', onCancel);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
      el?.removeEventListener('cancel', onCancel);
      if (el?.open) el.close();
    };
  }, []);

  // Na elke render: positie bijwerken (de hoogte verschilt per stap) en zo nodig de focus zetten.
  useLayoutEffect(() => {
    place();
    if (focusGrid.current) {
      focusGrid.current = false;
      const btn = ref.current?.querySelector<HTMLButtonElement>('[data-active="true"]');
      btn?.focus({ preventScroll: true });
    }
  });

  const pickHour = (h: number) => {
    setHour(h);
    setStage('minute');
    const idx = h === currentHour && currentMinute !== null ? minutes.indexOf(currentMinute) : -1;
    setActive(idx < 0 ? 0 : idx);
    focusGrid.current = true;
  };

  const backToHours = () => {
    setStage('hour');
    const idx = hour === null ? -1 : hours.indexOf(hour);
    setActive(idx < 0 ? 0 : idx);
    focusGrid.current = true;
  };

  const toggleNight = () => {
    const next = !night;
    const opts = hourOptions(next);
    const focused = hours[active];
    setNight(next);
    const idx = focused === undefined ? -1 : opts.indexOf(focused);
    setActive(idx < 0 ? 0 : idx);
  };

  const onGridKey = (count: number, cols: number) => (e: KeyboardEvent) => {
    if (!GRID_KEYS.includes(e.key)) return;
    e.preventDefault();
    const next = moveInGrid(active, e.key as GridKey, cols, count);
    setActive(next);
    focusGrid.current = true;
  };

  const building =
    stage === 'minute' && hour !== null ? `${pad2(hour)}:__` : value ? value : '__:__';
  const titleId = `${id}-title`;
  const stepId = `${id}-step`;

  const hourGrid = (
    <div
      role="grid"
      aria-label={labels.hours}
      class={cx(styles.grid, styles.hours)}
      onKeyDown={onGridKey(hours.length, HOUR_COLS)}
    >
      {chunk(hours, HOUR_COLS).map((row, r) => (
        <div role="row" class={styles.row} key={`h${r}-${row[0]}`}>
          {row.map((h, c) => {
            const i = r * HOUR_COLS + c;
            const isCurrent = h === currentHour;
            return (
              <div role="gridcell" key={h} class={styles.cell}>
                <button
                  type="button"
                  class={cx(
                    styles.cellButton,
                    isCurrent && styles.current,
                    (h < 6 || beforeStart(h, 59)) && styles.muted,
                  )}
                  tabIndex={i === active ? 0 : -1}
                  data-active={i === active ? 'true' : undefined}
                  data-hour={pad2(h)}
                  aria-current={isCurrent ? 'time' : undefined}
                  disabled={!hourAllowed(h)}
                  onClick={() => pickHour(h)}
                  onFocus={() => setActive(i)}
                >
                  {pad2(h)}
                </button>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  const minuteGrid =
    hour === null ? null : (
      <div
        role="grid"
        aria-label={`${labels.minutes} ${pad2(hour)}:__`}
        class={cx(styles.grid, styles.minutes)}
        onKeyDown={onGridKey(minutes.length, MINUTE_COLS)}
      >
        {chunk(minutes, MINUTE_COLS).map((row, r) => (
          <div role="row" class={cx(styles.row, r === 0 && styles.quarterRow)} key={`m${r}`}>
            {row.map((m, c) => {
              const i = r * MINUTE_COLS + c;
              const isCurrent = hour === currentHour && m === currentMinute;
              const text = `${pad2(hour)}:${pad2(m)}`;
              return (
                <div role="gridcell" key={m} class={styles.cell}>
                  <button
                    type="button"
                    class={cx(
                      styles.cellButton,
                      m % 15 === 0 ? styles.quarter : styles.five,
                      beforeStart(hour, m) && styles.muted,
                      isCurrent && styles.current,
                    )}
                    tabIndex={i === active ? 0 : -1}
                    data-active={i === active ? 'true' : undefined}
                    data-minute={pad2(m)}
                    aria-current={isCurrent ? 'time' : undefined}
                    disabled={!minuteAllowed(hour, m)}
                    onClick={() => onPick(text)}
                    onFocus={() => setActive(i)}
                  >
                    {text}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );

  return (
    <dialog
      ref={ref}
      id={id}
      class={styles.picker}
      aria-labelledby={`${titleId} ${stepId}`}
      data-name={`${id}`}
      data-stage={stage}
      data-quiet-focus={quiet ? '' : undefined}
      onKeyDown={() => {
        if (quiet) setQuiet(false);
      }}
      onClick={(e) => {
        // Klik op de ::backdrop komt binnen als klik op het dialog-element zelf.
        if (e.target === ref.current) closeRef.current();
      }}
    >
      <div class={styles.panel}>
        <div class={styles.head}>
          <div class={styles.headText}>
            <p id={titleId} class={styles.eyebrow}>
              {label}
            </p>
            <p class={styles.display} aria-hidden="true" data-name="time-picker-display">
              {building}
            </p>
            <h2 id={stepId} class={styles.step}>
              {stage === 'hour' ? labels.hourStep : labels.minuteStep}
            </h2>
          </div>
          <button
            type="button"
            class={styles.iconButton}
            aria-label={labels.close}
            title={labels.close}
            data-name="time-picker-close"
            onClick={() => closeRef.current()}
          >
            <Icon id="x" size={20} />
          </button>
        </div>

        {stage === 'hour' && durationChoices.length > 0 ? (
          <div class={styles.durationBlock}>
            <p class={styles.sectionLabel} id={`${id}-durations`}>
              {labels.durations}
            </p>
            <div
              class={styles.durations}
              role="group"
              aria-labelledby={`${id}-durations`}
              data-name="time-picker-durations"
            >
              {durationChoices.map(({ d, end }) => {
                const text = formatDuration(d, labels);
                const isCurrent = end !== null && end === value;
                return (
                  <button
                    key={d}
                    type="button"
                    class={cx(styles.duration, isCurrent && styles.current)}
                    disabled={end === null || !inRange(end, min, max)}
                    aria-label={labels.durationUntil
                      .replace('{duration}', text)
                      .replace('{time}', end ?? '')}
                    aria-pressed={isCurrent ? 'true' : 'false'}
                    data-duration={d}
                    onClick={() => end && onPick(end)}
                  >
                    <span class={styles.durationText}>{text}</span>
                    <span class={styles.durationEnd}>{end ?? '–'}</span>
                  </button>
                );
              })}
            </div>
            <p class={styles.sectionLabel}>{labels.orPickEnd}</p>
          </div>
        ) : null}

        {stage === 'hour' ? hourGrid : minuteGrid}

        <div class={styles.foot}>
          {stage === 'hour' ? (
            <button
              type="button"
              class={cx(styles.textButton, night && styles.pressed)}
              aria-pressed={night ? 'true' : 'false'}
              data-name="time-picker-night"
              onClick={toggleNight}
            >
              <Icon id="moon" size={18} />
              {labels.showNight}
            </button>
          ) : (
            <button
              type="button"
              class={styles.textButton}
              data-name="time-picker-back"
              onClick={backToHours}
            >
              <Icon id="chevron-left" size={18} />
              {labels.back}
            </button>
          )}
          <button
            type="button"
            class={styles.textButton}
            data-name="time-picker-type"
            onClick={() => {
              // Eerst dicht (niet meer inert), dan kan het veld binnen dezelfde tik de focus
              // krijgen en opent iOS het toetsenbord.
              ref.current?.close();
              onType();
            }}
          >
            <Icon id="pencil" size={18} />
            {labels.type}
          </button>
        </div>
      </div>
    </dialog>
  );
}
